"use client";

import dayjs, { type Dayjs } from "dayjs";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { FetchButton } from "@/fetch-ui/FetchButton";

import type { TripDates } from "../../_logic/types";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ISO = "YYYY-MM-DD";
/** Days moved by each grid key, per the WAI-ARIA date grid pattern. */
const KEY_STEPS: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

interface RangeCalendarProps {
  value: TripDates | null;
  /** Month to open on when there is no value, e.g. the first suggested window. */
  initialMonth?: string;
  /** Called once both ends are picked. */
  onChange: (dates: TripDates) => void;
}

/** The 6×7 grid of days shown for a month, starting on the Sunday on or before the 1st. */
const monthGrid = (month: Dayjs): Dayjs[] => {
  const first = month.startOf("month");
  const start = first.subtract(first.day(), "day");
  return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
};

/**
 * A one-month range calendar: tap the first night, then the last day. The month title opens a
 * month grid with year arrows, for a quick jump, and Today comes back to this month. Used by the
 * dates pill and inline in the chat, so both pick dates the same way. Opens on the chosen dates,
 * even past ones, so the planner sees what is set; past days and months can't be picked.
 * @param props - The current range and the change handler.
 * @returns The calendar.
 * @throws Never.
 */
export const RangeCalendar = ({ value, initialMonth, onChange }: RangeCalendarProps) => {
  // Hooks
  const grid = useRef<HTMLDivElement>(null);
  /** Set by a key move or a month pick, so only those pull focus into the grid. */
  const keyed = useRef(false);

  // States
  const [month, setMonth] = useState(() => {
    // Chosen dates open where they are, past or not; a suggested month never opens in the past.
    if (value) return dayjs(value.start).startOf("month");
    const opening = dayjs(initialMonth ?? undefined);
    return (opening.isBefore(dayjs(), "month") ? dayjs() : opening).startOf("month");
  });
  const [anchor, setAnchor] = useState<Dayjs | null>(null);
  const [hovered, setHovered] = useState<Dayjs | null>(null);
  /** The month grid is showing in place of the days. */
  const [picking, setPicking] = useState(false);
  // The one day in the tab order; arrow keys move it, and focus follows once the grid has it.
  const [focused, setFocused] = useState<Dayjs>(() => dayjs(value?.start ?? undefined).startOf("day"));

  // Derived
  const today = dayjs().startOf("day");
  const days = monthGrid(month);
  const start = anchor ?? (value ? dayjs(value.start) : null);
  const end = anchor ? (hovered && !hovered.isBefore(anchor) ? hovered : null) : value ? dayjs(value.end) : null;
  const canGoBack = picking ? month.year() > today.year() : month.isAfter(today, "month");
  const awayFromToday = !month.isSame(today, "month");
  const months = Array.from({ length: 12 }, (_, index) => month.month(index));
  const rovingDay = days.some((day) => day.isSame(focused, "day"))
    ? focused
    : (days.find((day) => day.isSame(month, "month") && !day.isBefore(today)) ?? month);
  const weeks = Array.from({ length: 6 }, (_, week) => days.slice(week * 7, week * 7 + 7));

  // Handlers
  const pick = (day: Dayjs) => {
    if (!anchor || day.isBefore(anchor)) {
      setAnchor(day);
      // A start picked from the next month's leading days moves the view there, so the end stays in reach.
      if (!day.isSame(month, "month")) setMonth(day.startOf("month"));
      return;
    }
    setAnchor(null);
    setHovered(null);
    onChange({ start: anchor.format(ISO), end: day.format(ISO) });
  };

  const moveFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = KEY_STEPS[event.key];
    const next =
      step !== undefined
        ? focused.add(step, "day")
        : event.key === "Home"
          ? focused.subtract(focused.day(), "day")
          : event.key === "End"
            ? focused.add(6 - focused.day(), "day")
            : event.key === "PageUp"
              ? focused.subtract(1, "month")
              : event.key === "PageDown"
                ? focused.add(1, "month")
                : null;
    if (!next) return;
    event.preventDefault();
    const target = next.isBefore(today) ? today : next;
    keyed.current = true;
    setFocused(target);
    setHovered(target);
    if (!target.isSame(month, "month")) setMonth(target.startOf("month"));
  };

  const pickMonth = (candidate: Dayjs) => {
    // The month button goes away with the month grid, so focus lands on the month's first open day.
    keyed.current = true;
    setFocused(candidate.isSame(today, "month") ? today : candidate.startOf("month"));
    setMonth(candidate);
    setPicking(false);
  };

  const goToToday = () => {
    keyed.current = true;
    setFocused(today);
    setMonth(today.startOf("month"));
    setPicking(false);
  };

  // Effects
  // Keyboard moves (including into another month) and month picks carry DOM focus to the new day,
  // once the day grid is back.
  useEffect(() => {
    if (!keyed.current || picking) return;
    keyed.current = false;
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${focused.format(ISO)}"]`)?.focus();
  }, [focused, month, picking]);

  // Render
  return (
    <div className="flex w-[280px] flex-col gap-2 select-none">
      <div className="flex items-center justify-between">
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          aria-label={picking ? "Previous year" : "Previous month"}
          disabled={!canGoBack}
          onClick={() => setMonth((current) => current.subtract(1, picking ? "year" : "month"))}
          className="flex size-8 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ChevronLeft className="size-4" />
        </button>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          aria-expanded={picking}
          title="Pick a month and year"
          onClick={() => setPicking((current) => !current)}
          className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <span aria-live="polite">{month.format("MMMM YYYY")}</span>
          <ChevronDown
            className={`size-3.5 text-gray-500 transition-transform duration-200 ${picking ? "rotate-180" : ""}`}
          />
        </button>
        {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
        <button
          type="button"
          aria-label={picking ? "Next year" : "Next month"}
          onClick={() => setMonth((current) => current.add(1, picking ? "year" : "month"))}
          className="flex size-8 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
      {picking ? (
        // The same height as the day grid, so the popover holds still while jumping.
        <div role="group" aria-label={`Months of ${month.year()}`} className="grid h-[252px] grid-cols-3 gap-1">
          {months.map((candidate) => {
            const past = candidate.isBefore(today, "month");
            return (
              // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
              <button
                key={candidate.month()}
                type="button"
                disabled={past}
                aria-pressed={candidate.isSame(month, "month")}
                aria-label={candidate.format("MMMM YYYY")}
                onClick={() => pickMonth(candidate)}
                className={`rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  past
                    ? // ui-lint-disable-next-line low-contrast-text: an inactive control; WCAG 1.4.3 exempts disabled text
                      "cursor-default text-gray-300"
                    : "text-gray-900 hover:bg-gray-100 aria-pressed:bg-indigo-600 aria-pressed:text-white"
                }`}
              >
                {candidate.format("MMM")}
              </button>
            );
          })}
        </div>
      ) : (
        <div
          ref={grid}
          role="grid"
          aria-label={month.format("MMMM YYYY")}
          onKeyDown={moveFocus}
          className="flex flex-col"
        >
          <div role="row" className="grid grid-cols-7 text-center text-xs font-medium text-gray-600">
            {WEEKDAYS.map((weekday) => (
              <abbr
                key={weekday}
                role="columnheader"
                title={weekday}
                aria-label={weekday}
                className="py-1 no-underline"
              >
                {weekday.slice(0, 2)}
              </abbr>
            ))}
          </div>
          {weeks.map((week) => (
            <div key={week[0]?.format(ISO)} role="row" className="grid grid-cols-7 pt-0.5">
              {week.map((day) => {
                const inMonth = day.isSame(month, "month");
                const disabled = day.isBefore(today);
                const isStart = start?.isSame(day, "day") ?? false;
                const isEnd = end?.isSame(day, "day") ?? false;
                const inRange = Boolean(start && end && day.isAfter(start, "day") && day.isBefore(end, "day"));
                const edge = isStart || isEnd;
                return (
                  <div
                    key={day.format(ISO)}
                    role="gridcell"
                    aria-selected={edge || inRange}
                    className={`flex justify-center ${inRange ? "bg-indigo-50" : ""} ${isStart && end && !isEnd ? "rounded-l-full bg-indigo-50" : ""} ${isEnd && start && !isStart ? "rounded-r-full bg-indigo-50" : ""}`}
                  >
                    {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
                    <button
                      type="button"
                      data-day={day.format(ISO)}
                      tabIndex={day.isSame(rovingDay, "day") ? 0 : -1}
                      disabled={disabled}
                      aria-label={day.format("dddd, MMMM D, YYYY")}
                      onClick={() => pick(day)}
                      onFocus={() => setFocused(day)}
                      onMouseEnter={() => setHovered(day)}
                      className={`flex size-9 items-center justify-center rounded-full text-sm font-medium tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                        edge
                          ? "bg-indigo-600 text-white"
                          : disabled
                            ? // ui-lint-disable-next-line low-contrast-text: an inactive control; WCAG 1.4.3 exempts disabled text
                              "cursor-default text-gray-300"
                            : inMonth
                              ? "text-gray-900 hover:bg-gray-100"
                              : "text-gray-400 hover:bg-gray-100"
                      }`}
                    >
                      {day.date()}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-600">
          {picking
            ? "Pick a month."
            : anchor
              ? "Now pick the day you leave."
              : "Pick the day you arrive, then the day you leave."}
        </p>
        {awayFromToday && (
          <FetchButton variant="text" size="sm" color="primary" type="button" onClick={goToToday}>
            Today
          </FetchButton>
        )}
      </div>
    </div>
  );
};
